import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

function getActivityHeight(value, maxValue) {
  if (maxValue <= 0) {
    return 0;
  }
  return Math.max(6, Math.round((value / maxValue) * 100));
}

/**
 * Graphiques d'évolution des statistiques stream.
 * @param {Object} props
 * @param {Array} props.timeline
 * @param {string} props.titleRange
 * @returns {JSX.Element|null}
 */
function StreamEvolutionCharts({ timeline, titleRange }) {
  const safeTimeline = Array.isArray(timeline) ? timeline : [];
  const chartData = useMemo(() => safeTimeline.slice(-12), [safeTimeline]);

  const totals = useMemo(
    () =>
      chartData.reduce(
        (accumulator, day) => {
          accumulator.follows += day.follows;
          accumulator.unfollows += day.unfollows;
          accumulator.subs += day.subs;
          return accumulator;
        },
        { follows: 0, unfollows: 0, subs: 0 }
      ),
    [chartData]
  );

  const maxDailyCount = useMemo(
    () =>
      Math.max(
        1,
        ...chartData.flatMap((day) => [day.follows, day.unfollows, day.subs])
      ),
    [chartData]
  );

  const maxAbsNet = useMemo(
    () => Math.max(1, ...chartData.map((day) => Math.abs(day.netFollowers))),
    [chartData]
  );

  if (chartData.length === 0) {
    return null;
  }

  return (
    <section className="stream-evolution-section">
      <div className="stream-evolution-header">
        <h3 className="stream-evolution-title">Evolution du stream</h3>
        <p className="stream-evolution-subtitle">
          Tendances journalières sur les {titleRange}
        </p>
      </div>

      <div className="stream-evolution-grid">
        <article className="stream-chart-card">
          <div className="stream-chart-head">
            <h4>Activité quotidienne</h4>
            <p>Follows, unfollows et abonnements</p>
          </div>

          <div className="stream-chart-legend">
            <span className="legend-item">
              <i className="legend-dot follows" />
              Follows ({totals.follows})
            </span>
            <span className="legend-item">
              <i className="legend-dot unfollows" />
              Unfollows ({totals.unfollows})
            </span>
            <span className="legend-item">
              <i className="legend-dot subs" />
              Subs ({totals.subs})
            </span>
          </div>

          <div className="stream-bars-chart">
            {chartData.map((day) => (
              <div
                key={day.dateKey}
                className="stream-bars-day"
                title={`${day.label} • follows: ${day.follows}, unfollows: ${day.unfollows}, subs: ${day.subs}`}
              >
                <div className="stream-bars-stack">
                  <span
                    className="stream-bar follows"
                    style={{
                      height: `${getActivityHeight(day.follows, maxDailyCount)}%`,
                    }}
                  />
                  <span
                    className="stream-bar unfollows"
                    style={{
                      height: `${getActivityHeight(day.unfollows, maxDailyCount)}%`,
                    }}
                  />
                  <span
                    className="stream-bar subs"
                    style={{
                      height: `${getActivityHeight(day.subs, maxDailyCount)}%`,
                    }}
                  />
                </div>
                <span className="stream-day-label">{day.label}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="stream-chart-card">
          <div className="stream-chart-head">
            <h4>Croissance nette des followers</h4>
            <p>Follows - unfollows, jour par jour</p>
          </div>

          <div className="stream-net-chart">
            <div className="stream-net-axis" />
            {chartData.map((day) => {
              const net = day.netFollowers;
              const height = Math.max(
                4,
                Math.round((Math.abs(net) / maxAbsNet) * 48)
              );
              const isPositive = net >= 0;
              return (
                <div
                  key={`net-${day.dateKey}`}
                  className="stream-net-day"
                  title={`${day.label} • net followers: ${net}`}
                >
                  <div className="stream-net-column">
                    <span
                      className={`stream-net-bar ${isPositive ? 'positive' : 'negative'}`}
                      style={{
                        height: `${height}%`,
                        [isPositive ? 'bottom' : 'top']: '50%',
                      }}
                    />
                  </div>
                  <span className="stream-day-label">{day.label}</span>
                </div>
              );
            })}
          </div>
        </article>
      </div>
    </section>
  );
}

StreamEvolutionCharts.propTypes = {
  timeline: PropTypes.arrayOf(
    PropTypes.shape({
      dateKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      follows: PropTypes.number.isRequired,
      unfollows: PropTypes.number.isRequired,
      subs: PropTypes.number.isRequired,
      netFollowers: PropTypes.number.isRequired,
    })
  ),
  titleRange: PropTypes.string.isRequired,
};

export default StreamEvolutionCharts;
